package com.elan.edubridge.ui.shell

import com.elan.edubridge.data.firebase.WebAuth
import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.policy.AgePolicy
import com.elan.edubridge.shared.domain.policy.SignupAgeVerdict
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement

/** `/login`, `/signup` — 자격증명과 가입 프로필을 분리해 Google·이메일 가입이 같은 정보를 수집한다. */
object AuthPage {
    private val scope = MainScope()
    private val accountRepository = SharedGraph.createAccountRepository()

    private enum class Mode { LOGIN, SIGNUP }
    private enum class SignupMethod { EMAIL, GOOGLE }

    fun renderLogin() = renderCredentials(Mode.LOGIN)

    fun renderSignup() = renderCredentials(Mode.SIGNUP)

    private fun renderCredentials(mode: Mode, errorMessage: String? = null) {
        renderPage("eb-auth", credentialsHtml(mode, errorMessage))

        val form = document.getElementById("auth-form") as HTMLFormElement
        form.onsubmit = { event ->
            event.preventDefault()
            submitCredentials(mode)
            null
        }

        val googleButton = document.getElementById("google-submit") as HTMLButtonElement
        googleButton.onclick = {
            submitGoogleCredentials(mode)
            null
        }
    }

    private fun renderSignupProfile(
        method: SignupMethod,
        email: String? = null,
        password: String? = null,
        errorMessage: String? = null,
    ) {
        renderPage("eb-auth", signupProfileHtml(method, errorMessage))

        val form = document.getElementById("signup-profile-form") as HTMLFormElement
        form.onsubmit = { event ->
            event.preventDefault()
            submitSignupProfile(method, email, password)
            null
        }
        (document.getElementById("signup-profile-back") as HTMLButtonElement).onclick = {
            renderCredentials(Mode.SIGNUP)
            null
        }
    }

    private fun submitCredentials(mode: Mode) {
        val email = (document.getElementById("email") as HTMLInputElement).value
        val password = (document.getElementById("password") as HTMLInputElement).value
        if (email.isBlank() || password.isBlank()) {
            renderCredentials(mode, "필수 항목을 모두 입력해 주세요.")
            return
        }
        if (mode == Mode.SIGNUP) {
            if (!hasAcceptedTerms()) {
                renderCredentials(mode, "약관에 동의해야 계정을 만들 수 있어요.")
                return
            }
            renderSignupProfile(SignupMethod.EMAIL, email, password)
            return
        }

        (document.getElementById("submit") as HTMLButtonElement).disabled = true
        scope.launch {
            val result = accountRepository.login(email, password)
            if (!result.isSuccess) renderCredentials(mode, result.errorMessage)
        }
    }

    /** 기존 계정 Google 로그인은 이름·생년월일 없이 유지하고, 신규 가입은 프로필 단계로 보낸다. */
    private fun submitGoogleCredentials(mode: Mode) {
        if (!hasAcceptedTerms()) {
            renderCredentials(mode, "약관에 동의해야 계정을 만들 수 있어요.")
            return
        }
        if (mode == Mode.SIGNUP) {
            renderSignupProfile(SignupMethod.GOOGLE)
            return
        }

        (document.getElementById("google-submit") as HTMLButtonElement).disabled = true
        scope.launch {
            val result = WebAuth.signInWithGoogle(registrationName = null, birthDate = null)
            if (!result.isSuccess) {
                if (result.errorMessage == GOOGLE_SIGNUP_DETAILS_REQUIRED) {
                    renderSignupProfile(SignupMethod.GOOGLE, errorMessage = result.errorMessage)
                } else {
                    renderCredentials(mode, result.errorMessage)
                }
            } else {
                Router.replace("/app")
            }
        }
    }

    private fun submitSignupProfile(method: SignupMethod, email: String?, password: String?) {
        val name = (document.getElementById("name") as HTMLInputElement).value
        val birthDate = (document.getElementById("birth-date") as HTMLInputElement).value
        val validationError = signupProfileValidationError(name, birthDate)
        if (validationError != null) {
            renderSignupProfile(method, email, password, validationError)
            return
        }

        (document.getElementById("signup-profile-submit") as HTMLButtonElement).disabled = true
        scope.launch {
            val result = when (method) {
                SignupMethod.EMAIL -> accountRepository.register(email.orEmpty(), password.orEmpty(), name, birthDate)
                SignupMethod.GOOGLE -> WebAuth.signInWithGoogle(name, birthDate)
            }
            if (!result.isSuccess) {
                renderSignupProfile(method, email, password, result.errorMessage)
            } else if (method == SignupMethod.GOOGLE) {
                Router.replace("/app")
            }
        }
    }

    private fun hasAcceptedTerms(): Boolean =
        (document.getElementById("terms") as? HTMLInputElement)?.checked == true

    private fun credentialsHtml(mode: Mode, errorMessage: String?): String {
        val isSignup = mode == Mode.SIGNUP
        return """
            <a class="eb-auth__brand" href="/">${icon("icon-bridge-logo", "lg")}EduBridge</a>
            <article class="eb-auth-card">
                <h1>${if (isSignup) "회원가입" else "로그인"}</h1>
                <form id="auth-form" novalidate>
                    <div class="eb-field">
                        <label for="email">이메일</label>
                        <input id="email" type="email" autocomplete="email" required aria-describedby="email-error">
                        <p id="email-error" class="eb-field__error" role="alert" hidden></p>
                    </div>
                    <div class="eb-field">
                        <label for="password">비밀번호</label>
                        <input id="password" type="password" autocomplete="${if (isSignup) "new-password" else "current-password"}" required>
                    </div>
                    <div class="eb-field eb-field--checkbox">
                        <input id="terms" type="checkbox" required>
                        <label for="terms">이용약관과 개인정보처리방침에 동의합니다.</label>
                    </div>
                    ${errorMessage?.let { """<p class="eb-form-error" role="alert">${it.escapeHtml()}</p>""" } ?: ""}
                    <button type="submit" id="submit" class="eb-button eb-button--primary eb-button--block">${if (isSignup) "다음" else "로그인"}</button>
                </form>
                <div class="eb-auth-divider"><span>또는</span></div>
                <button type="button" id="google-submit" class="eb-button eb-button--social eb-button--block" aria-label="Google로 계속하기">
                    ${icon("icon-google", "md")}
                    <span>${if (isSignup) "Google로 가입" else "Google로 계속하기"}</span>
                </button>
                ${if (isSignup) """<p class="eb-meta">다음 단계에서 이름과 생년월일을 입력해요.</p>""" else ""}
                <p class="eb-auth-card__switch">${
                    if (isSignup) """이미 계정이 있으신가요? <a href="/login">로그인</a>""" else """계정이 없으신가요? <a href="/signup">새 계정 만들기</a>"""
                }</p>
            </article>
        """.trimIndent()
    }

    private fun signupProfileHtml(method: SignupMethod, errorMessage: String?): String = """
        <a class="eb-auth__brand" href="/">${icon("icon-bridge-logo", "lg")}EduBridge</a>
        <article class="eb-auth-card">
            <h1>가입 정보</h1>
            <p class="eb-meta">학원 회원관리에 사용할 이름과 생년월일을 입력해 주세요.</p>
            <form id="signup-profile-form" novalidate>
                <div class="eb-field">
                    <label for="name">이름</label>
                    <input id="name" autocomplete="name" required>
                </div>
                <div class="eb-field">
                    <label for="birth-date">생년월일</label>
                    <input id="birth-date" type="date" autocomplete="bday" value="${defaultBirthDate()}" max="${latestBirthDateForMinimumAge()}" required>
                    <p class="eb-meta">만 ${AgePolicy.MINIMUM_SIGNUP_AGE}세 이상만 가입할 수 있어요.</p>
                </div>
                ${errorMessage?.let { """<p class="eb-form-error" role="alert">${it.escapeHtml()}</p>""" } ?: ""}
                <button type="submit" id="signup-profile-submit" class="eb-button eb-button--primary eb-button--block">${if (method == SignupMethod.GOOGLE) "Google로 가입" else "학생으로 가입"}</button>
            </form>
            <button type="button" id="signup-profile-back" class="eb-button eb-button--link">이전</button>
        </article>
    """.trimIndent()

    private fun signupProfileValidationError(name: String, birthDate: String): String? {
        if (name.isBlank()) return "이름을 입력해 주세요."
        return when (AgePolicy.validateSignupBirthDate(birthDate, todayIsoDate())) {
            SignupAgeVerdict.ELIGIBLE -> null
            SignupAgeVerdict.TOO_YOUNG -> "만 ${AgePolicy.MINIMUM_SIGNUP_AGE}세 이상만 가입할 수 있어요."
            SignupAgeVerdict.IN_FUTURE, SignupAgeVerdict.MALFORMED -> "올바른 생년월일을 입력해 주세요."
        }
    }
}

private const val GOOGLE_SIGNUP_DETAILS_REQUIRED = "Google 신규 가입은 회원가입 화면에서 이름과 생년월일을 입력해 주세요."

private fun todayIsoDate(): String = js(
    """(() => {
        const date = new Date();
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    })()""",
) as String

private fun defaultBirthDate(): String = js(
    """(() => {
        const date = new Date();
        date.setFullYear(date.getFullYear() - 16);
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    })()""",
) as String

private fun latestBirthDateForMinimumAge(): String = js(
    """(() => {
        const date = new Date();
        date.setFullYear(date.getFullYear() - ${AgePolicy.MINIMUM_SIGNUP_AGE});
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    })()""",
) as String
